/**
 * VaultRefsLine.jsx — Renders vault document attachments as clickable hyperlinks
 * inside the resume preview/PDF.
 *
 * Designed to be embedded at the bottom of any renderer block
 * (ExperienceBlock, EducationBlock, ProjectBlock, etc.)
 *
 * Each ref renders as:  📎 [label]
 * In PDF export:        the href="/api/vault/public/<uuid>" becomes a real clickable link.
 *
 * DO NOT use QR codes. Hyperlinks only.
 *
 * Usage:
 *   import { VaultRefsLine } from './VaultRefsLine';
 *   <VaultRefsLine refs={item.vault_refs} />
 *
 * Props:
 *   refs        — array of { doc_id, public_uuid, label, is_public }
 *   accentColor — optional CSS color string for the link color (falls back to --color-primary)
 *   fontSize    — optional font size string (default: '7.5pt')
 */
import React from 'react';

export function VaultRefsLine({ refs, accentColor, fontSize = '7.5pt' }) {
  if (!refs || refs.length === 0) return null;

  // Only show public documents in the rendered resume
  const publicRefs = refs.filter(r => r.is_public !== false && r.public_uuid);
  if (publicRefs.length === 0) return null;

  const linkColor = accentColor || 'var(--color-primary, #2563EB)';

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px 14px',
      marginTop: '5px',
      paddingTop: '4px',
      borderTop: '1px solid var(--color-border, rgba(0,0,0,0.08))',
      alignItems: 'center',
    }}>
      {publicRefs.map((ref, idx) => (
        <a
          key={idx}
          href={`/api/vault/public/${ref.public_uuid}`}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            fontSize,
            color: linkColor,
            textDecoration: 'none',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          <span style={{ fontSize: '0.9em', opacity: 0.7 }}>📎</span>
          {ref.label || 'View Document'}
        </a>
      ))}
    </div>
  );
}

export default VaultRefsLine;
